import React, { useState, useCallback } from 'react'
import { Row, InputNumber, Col, Typography, message } from 'antd'
import { PlusCircleFilled } from '@ant-design/icons'
import PropTypes from 'prop-types'
import FiltersLayout from '../component/FiltersLayout'
import TagGroup from '../../../components/TagGroup'
import { AddButton } from './styled'
import StringFilter from '../helpers/filterString'
import { setIsOpen } from '../helpers/actionHelpers'
import './sections.css'

const { Title } = Typography

const VALIDATION_RULES = {
  MAX_VALUE: 100,
  MIN_VALUE: 0,
  DEFAULT_MIN: 0,
  DEFAULT_MAX: 0
}

const ExperienceFilter = ({ setExistedFilters, existedFilters }) => {
  const [values, setValues] = useState({
    experienceMin: undefined,
    experienceMax: undefined,
    countMin: undefined,
    countMax: undefined,
    isOpen: false
  })

  const experienceData = existedFilters?.experience
  const cnt = StringFilter(experienceData?.count?.mustHave)
  const yrs = StringFilter(experienceData?.years?.mustHave)

  const handleChange = useCallback((field, value) => {
    setValues((prev) => ({
      ...prev,
      [field]: value ?? undefined
    }))
  }, [])

  const validateRange = useCallback((min, max, fieldName) => {
    if (!min && !max) return false
    if (max > 0 && min > max) {
      message.error(`Minimum ${fieldName} must be less than or equal to maximum`)
      return false
    }
    if (max > VALIDATION_RULES.MAX_VALUE) {
      message.error(`Maximum ${fieldName} must be ${VALIDATION_RULES.MAX_VALUE} or less`)
      return false
    }
    return true
  }, [])

  const buildRangeString = (min, max) => {
    const parts = []
    if (min) parts.push(`GTE ${min}`)
    if (max) parts.push(`LTE ${max}`)
    return parts.join(':')
  }

  const handleAdd = useCallback(
    (activeInput) => {
      const isExperience = activeInput === 'experience'
      const min = isExperience ? values.experienceMin : values.countMin
      const max = isExperience ? values.experienceMax : values.countMax
      const fieldName = isExperience ? 'years of experience' : 'job count'
      const isValid = validateRange(min, max, fieldName)
      if (isValid) {
        const rangeValue = buildRangeString(min, max)
        const updatedExperience = {
          ...existedFilters?.experience,
          ...(isExperience
            ? { years: { mustHave: rangeValue } }
            : { count: { mustHave: rangeValue } })
        }

        setExistedFilters({
          ...existedFilters,
          experience: updatedExperience
        })

        setValues((prev) => ({
          ...prev,
          countMin: undefined,
          countMax: undefined,
          experienceMin: undefined,
          experienceMax: undefined
        }))
      }
    },
    [
      values.experienceMin,
      values.countMin,
      values.experienceMax,
      values.countMax,
      validateRange,
      setExistedFilters,
      existedFilters
    ]
  )

  const preventDecimalInput = (e) => {
    if (e.key === '.') {
      e.preventDefault()
    }
  }

  const toggleOpen = useCallback(() => {
    setIsOpen(setValues, values)
  }, [values])

  const clearExperienceFilters = () => {
    const prev = existedFilters?.experience || {}
    const { years, ...rest } = prev

    setExistedFilters({
      ...existedFilters,
      experience: rest
    })
  }

  const clearJobCountFilters = () => {
    const prev = existedFilters?.experience || {}
    const { count, ...rest } = prev

    setExistedFilters({
      ...existedFilters,
      experience: rest
    })
  }

  const hasExperienceValues = yrs?.GTE || yrs?.LTE
  const hasJobCountValues = cnt?.GTE || cnt?.LTE
  return (
    <FiltersLayout title="EXPERIENCE" setIsOpen={toggleOpen} isOpen={values.isOpen}>
      {!hasExperienceValues && !hasJobCountValues && !values.isOpen && (
        <Title level={4} disabled style={{ fontSize: '14px', marginBottom: 0 }}>
          All Experience Levels
        </Title>
      )}
      <>
        {!!hasExperienceValues && (
          <>
            <Row className="exp-typo-row">
              <Title level={5}>Years of Experience</Title>
            </Row>
            <Row>
              <Col className="exp-tags">
                <TagGroup
                  tags={
                    yrs?.GTE && !yrs?.LTE
                      ? [`Min : ${yrs?.GTE}`]
                      : yrs?.GTE && yrs?.LTE
                      ? [`${yrs?.GTE} - ${yrs?.LTE}`]
                      : [`Max : ${yrs?.LTE}`]
                  }
                  onDelete={clearExperienceFilters}
                />
              </Col>
            </Row>
          </>
        )}
        {!!hasJobCountValues && (
          <>
            <Row className="job-count exp-typo-row" style={{ marginTop: 0 }}>
              <Title level={5} style={{ marginTop: 0 }}>
                Job Count
              </Title>
            </Row>
            <Row>
              <Col className="exp-tags">
                <TagGroup
                  tags={
                    cnt?.GTE && !cnt?.LTE
                      ? [`Min : ${cnt?.GTE}`]
                      : cnt?.GTE && cnt?.LTE
                      ? [`${cnt?.GTE} - ${cnt?.LTE}`]
                      : [`Max : ${cnt?.LTE}`]
                  }
                  onDelete={clearJobCountFilters}
                />
              </Col>
            </Row>
          </>
        )}
        {values.isOpen && (
          <>
            <Row className="exp-typo-row">
              <Title level={5}>Years of Experience</Title>
            </Row>
            <Row>
              <Row gutter={10} className="hide-input-number-arrows">
                <Col span={12}>
                  <Row align="middle">
                    <Col span={4}>Min</Col>
                    <Col span={2}>
                      <InputNumber
                        min={VALIDATION_RULES.MIN_VALUE}
                        max={VALIDATION_RULES.MAX_VALUE}
                        style={{ margin: '0 16px' }}
                        step={1}
                        value={values.experienceMin}
                        onChange={(value) => handleChange('experienceMin', value)}
                        onKeyDown={preventDecimalInput}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={12}>
                  <Row align="middle">
                    <Col span={4}>Max</Col>
                    <Col span={4}>
                      <InputNumber
                        min={VALIDATION_RULES.MIN_VALUE}
                        max={VALIDATION_RULES.MAX_VALUE}
                        style={{ margin: '0 16px' }}
                        step={1}
                        value={values.experienceMax}
                        onChange={(value) => handleChange('experienceMax', value)}
                        onPressEnter={() => handleAdd('experience')}
                        onKeyDown={preventDecimalInput}
                      />
                    </Col>
                  </Row>
                </Col>
                <AddButton
                  onClick={() => handleAdd('experience')}
                  icon={<PlusCircleFilled />}
                  style={{ marginBottom: 10 }}
                >
                  <span>Add</span>
                </AddButton>
              </Row>
            </Row>
          </>
        )}
        {values.isOpen && (
          <>
            <Row className="job-count exp-typo-row" style={{ marginTop: 0 }}>
              <Title level={5} style={{ marginTop: 0 }}>
                Job Count
              </Title>
            </Row>
            <Row gutter={10}>
              <Col span={12}>
                <Row align="middle">
                  <Col span={4}>Min</Col>
                  <Col span={2}>
                    <InputNumber
                      min={VALIDATION_RULES.MIN_VALUE}
                      max={VALIDATION_RULES.MAX_VALUE}
                      style={{ margin: '0 16px' }}
                      step={1}
                      value={values.countMin}
                      onChange={(value) => handleChange('countMin', value)}
                      onKeyDown={preventDecimalInput}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Row align="middle">
                  <Col span={4}>Max</Col>
                  <Col span={4}>
                    <InputNumber
                      min={VALIDATION_RULES.MIN_VALUE}
                      max={VALIDATION_RULES.MAX_VALUE}
                      style={{ margin: '0 16px' }}
                      step={1}
                      value={values.countMax}
                      onChange={(value) => handleChange('countMax', value)}
                      onPressEnter={() => handleAdd('job_count')}
                      onKeyDown={preventDecimalInput}
                    />
                  </Col>
                </Row>
              </Col>
              <AddButton onClick={() => handleAdd('job_count')} icon={<PlusCircleFilled />}>
                <span>Add</span>
              </AddButton>
            </Row>
          </>
        )}
      </>
    </FiltersLayout>
  )
}

ExperienceFilter.propTypes = {
  setExistedFilters: PropTypes.func,
  existedFilters: PropTypes.object
}

export default ExperienceFilter
